import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs, setDoc, writeBatch } from 'firebase/firestore';

import { saveUserLog } from './LogandUtils';
import { useUser } from '../../store/UserProvider';

import styleClasses from './css/MyPage.module.scss';

function PushPopup({
  open,
  setOpenPopup,
  attribute,            // "activity", "contact", "members", "positions" 등
  selectedItem,         // 단일 선택
  setSelectedItem,      // 단일 선택(팝업 닫은 후 초기화)
  selectedItems,        // 선택된 항목 ID들의 배열 (예: ["id1", "id2", ...])
  categoryOptions = [], // 선택 옵션이 필요할 경우 (예: activity의 경우 "stages", "events")
  defaultCategory = '', // 기본 카테고리값 (없으면 빈 문자열)
  fetchData,            // 작업 후 데이터를 새로 불러오는 함수
  setOpenAlertPopup,    // alert 팝업을 열기 위한 함수
  setPopupTitle,        // 팝업 제목 설정 함수
  setPopupButtonText,   // 팝업 버튼 텍스트 설정 함수
  setPopupFunction,     // 팝업 버튼 클릭 시 실행할 함수 설정
  clearSelectedItems,  // 부모에서 선택 상태 초기화를 위한 함수 (예: setCheckedIDs)
}) {
  const { user, userLoading } = useUser();
  
  const navigate      = useNavigate();
  const isSubmitting  = useRef(false);
  
  const [userID, setUserID]                     = useState('');
  const [showProgress, setShowProgress]         = useState(false);
  const [progress, setProgress]                 = useState(0);
  const [musicards, setMusicards]               = useState([]);
  const [selectedMusicard, setSelectedMusicard] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [buttonLoading, setButtonLoading]       = useState(false);
  const [active, setActive]                     = useState(open ? styleClasses.active : '');

  // 필요한 props가 전달되지 않았을 경우, early return 대신 조건부 렌더링합니다.
  const missingProps  = !attribute || !selectedItems;
  
  // 팝업 열릴 때 active 클래스 설정
  useEffect(() => {
    setActive(open ? styleClasses.active : '');
    setSelectedMusicard('');
    setSelectedCategory('');
    setShowProgress(false);
    setProgress(0);
    // 초기화
    if (open === false) {
      setSelectedItem([]);
    }
  }, [open, setSelectedItem]);

  // 🔹 사용자 문서에서 뮤지카드 목록 불러오기
  const fetchMusicards = useCallback(async () => {
    try {
      const userDocSnap = await getDoc(doc(db, "users", userID));
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        let musicardEntries = Object.entries(userData.musicard || {});

        // attribute가 "positions"이면 "solo" 포함, "members"이면 "team" 포함
        if (attribute === "positions") {
          musicardEntries = musicardEntries.filter(([key]) => key.includes("solo"));
        } else if (attribute === "members") {
          musicardEntries = musicardEntries.filter(([key]) => key.includes("team"));
        }
        const musicardList = musicardEntries.map(([key, value]) => ({
          id: key,
          name: value,
        }));
        setMusicards(musicardList);
        console.log(musicardList);
      } else {
        setSelectedMusicard('');
        setOpenPopup(false);
        setOpenAlertPopup(true);
        setPopupTitle('사용자 문서가 존재하지 않습니다.');
        setPopupButtonText('');
        setPopupFunction(() => {});
      }
    } catch (error) {
      console.error("Error fetching musicards:", error);
      setSelectedMusicard('');
      setOpenPopup(false);
      setOpenAlertPopup(true);
      setPopupTitle('뮤지카드 정보를 불러오는 중 오류가 발생했습니다.');
      setPopupButtonText('');
      setPopupFunction(() => {});
    }
  }, [attribute, setOpenAlertPopup, setOpenPopup, setPopupButtonText, setPopupFunction, setPopupTitle, userID]);
  
  // 🔹 처리 함수: 선택된 각 항목을 뮤지카드로 복사/푸시
  const handleSubmit = async () => {
    // "unreleased"인 경우는 고정된 'unreleasedAlbums', "released"인 경우에는 사용자가 선택한 selectedType 사용
    const targetType = attribute === 'unreleased'
      ? 'unreleasedAlbums'
      : selectedCategory;  // released일 때 사용자가 선택한 값 (예: 'ownAlbums' 또는 'collaboAlbums')

    // 개별 선택일 경우 selectedItem
    if (selectedItem.length) {
      selectedItems = selectedItem;
    } else if (!selectedMusicard || !selectedItems || selectedItems.length === 0) {
      setOpenAlertPopup(true);
      setPopupTitle(`푸시할 ${attribute} 데이터를 선택해주세요.`);
      setPopupButtonText('');
      setPopupFunction(() => {});
      return;
    }

    try {
      if (isSubmitting.current) return;
      isSubmitting.current = true;
      setButtonLoading(true);
      
      let allItemsLength  = selectedItems.length;
      let currentProgress = 1;
      if (allItemsLength > 2) {
        setShowProgress(true);
        setTimeout(() => {
          setProgress(((currentProgress / allItemsLength) * 100).toFixed(2));
        }, 100);
      }

      // 각 선택된 item에 대해 분기 처리
      for (const item of selectedItems)
      {
        const itemId = typeof item === 'string' ? item : (item && item.id ? item.id : null);
        if (!itemId) {
          console.error("Invalid item in selectedItems:", item);
          continue;
        }

        // PushAlbum (앨범 내보내기) 로직 – attribute가 released 또는 unreleased인 경우
        if (attribute === 'released' || attribute === 'unreleased') {
          console.log("앨범 내보내기");
          
          // 1. users_data에서 해당 앨범 데이터 가져오기
          const userAlbumRef = doc(db, `users_data/${userID}/${attribute}`, itemId);
          const albumSnap = await getDoc(userAlbumRef);
          if (!albumSnap.exists()) {
              setOpenAlertPopup(true);
              setPopupTitle(`복사할 앨범 데이터를 찾을 수 없습니다: ${itemId}`);
              setPopupButtonText('');
              setPopupFunction(() => {});
              return;
          }
          const albumData = albumSnap.data();
      
          // 2. 가져온 앨범 데이터에 albumId와 ownerId 추가하여 musicians 컬렉션에 저장
          const albumRef = doc(db, `musicians/${selectedMusicard}/${targetType}`, itemId);
          await setDoc(albumRef, { 
              ...albumData,
              albumId: itemId,
              ownerId: userID,
          }, { merge: true });
      
          // 3. users_data의 해당 앨범의 트랙 정보를 musicians의 앨범 하위 컬렉션으로 복사
          const sourceTracksRef = collection(db, `users_data/${userID}/${attribute}/${itemId}/tracks`);
          const tracksSnapshot = await getDocs(sourceTracksRef);
          const batch = writeBatch(db);
          tracksSnapshot.forEach((trackDoc) => {
              const trackData = trackDoc.data();
              const destTrackRef = doc(db, `musicians/${selectedMusicard}/${targetType}/${itemId}/tracks`, trackDoc.id);
              batch.set(destTrackRef, trackData);
          });
          await batch.commit();
      
          const changedPaths = [albumRef.path, sourceTracksRef.path];
          await saveUserLog('musicard_logs', selectedMusicard, 'create', changedPaths, 'PushAlbum');
      
          // 4. users_data의 해당 앨범 문서에서 act 필드 업데이트 (병합)
          await setDoc(userAlbumRef, {
              act: { [selectedMusicard]: targetType }
          }, { merge: true });
        } else if (attribute === 'activity') {
          console.log("activity 내보내기")
              // 1. users_data에서 해당 액티비티 데이터 가져오기
              const sourceRef = doc(db, `users_data/${ userID }/activity`, itemId);
              const sourceSnap = await getDoc(sourceRef);
              if (!sourceSnap.exists()) {
                setOpenAlertPopup(true);
                setPopupTitle(`액티비티 데이터를 찾을 수 없습니다: ${itemId}`);
                setPopupButtonText('');
                setPopupFunction(() => {});
                continue;
              }
              const sourceData = sourceSnap.data();

              console.log(`users_data/${ userID }/activity 가져오기 완료`)

          
              // 대상 경로: musicians/{selectedMusicard}/{selectedCategory 혹은 "activity"}/{itemId}
              const targetCategory = selectedCategory || "activity";
              const targetRef = doc(db, `musicians/${selectedMusicard}/${targetCategory}`, itemId);
              await setDoc(targetRef, {
                ...sourceData,
                activityId: itemId,
                ownerId: userID,
              }, { merge: true });
              console.log(`${targetRef.path} 저장 완료`)


              await saveUserLog('musicard_logs', selectedMusicard, 'create', targetRef.path, 'PushActivity');
              // 업데이트: users_data 액티비티 문서 act 필드 병합
              await setDoc(sourceRef, {
                act: { [selectedMusicard]: targetCategory }
              }, { merge: true });
              //break;
        } else if (attribute === 'contact') {
          console.log("contact 내보내기")
          // 1. users_data에서 해당 연락처 데이터 가져오기
          const sourceRef = doc(db, `users_data/${ userID }/contact`, itemId);
          const sourceSnap = await getDoc(sourceRef);
          if (!sourceSnap.exists()) {
            setOpenAlertPopup(true);
            setPopupTitle(`연락처 데이터를 찾을 수 없습니다: ${itemId}`);
            setPopupButtonText('');
            setPopupFunction(() => {});
            continue;
          }
          const sourceData = sourceSnap.data();
          // 2. personal 데이터 추출
          const personalData = {
            name: sourceData.name,
            email: sourceData.email,
            introduction: sourceData.introduction,
            musicLinks: sourceData.musicLinks !== undefined ? sourceData.musicLinks : []
          };
          const targetRef = doc(db, `musicians/${selectedMusicard}/contact`, "personal");
          await setDoc(targetRef, personalData, { merge: true });
          // 3. location 데이터가 있으면 복사 (배열이어야 함)
          if (sourceData.location && Array.isArray(sourceData.location)) {
            const locationObj = {};
            sourceData.location.forEach((loc, index) => {
              locationObj[`${index + 1}`] = loc;
            });
            const locationRef = doc(db, `musicians/${selectedMusicard}/contact`, "location");
            await setDoc(locationRef, locationObj, { merge: true });
            await saveUserLog('musicard_logs', selectedMusicard, 'create', locationRef.path, 'PushContact');
          }
          await saveUserLog('musicard_logs', selectedMusicard, 'create', targetRef.path, 'PushContact');
          await setDoc(sourceRef, {
            act: { [selectedMusicard]: itemId }
          }, { merge: true });
          break;
        } else if (attribute === 'members') {
          console.log("members 내보내기")

          // 1. users_data에서 해당 멤버 데이터 가져오기
          const sourceRef = doc(db, `users_data/${ userID }/members`, itemId);
          const sourceSnap = await getDoc(sourceRef);
          if (!sourceSnap.exists()) {
            setOpenAlertPopup(true);
            setPopupTitle(`멤버 데이터를 찾을 수 없습니다: ${itemId}`);
            setPopupButtonText('');
            setPopupFunction(() => {});
            continue;
          }
          const sourceData = sourceSnap.data();
          const targetRef = doc(db, `musicians/${selectedMusicard}/members`, itemId);
          await setDoc(targetRef, {
            ...sourceData,
            memberId: itemId,
            ownerId: userID,
          }, { merge: true });
          await saveUserLog('musicard_logs', selectedMusicard, 'create', targetRef.path, 'PushMember');
          await setDoc(sourceRef, {
            act: { [selectedMusicard]: itemId }
          }, { merge: true });
          break;
        } else if (attribute === 'positions') {
          console.log("positions 내보내기")

          // 1. users_data에서 해당 포지션 데이터 가져오기
          const sourceRef = doc(db, `users_data/${ userID }/positions`, itemId);
          const sourceSnap = await getDoc(sourceRef);
          if (!sourceSnap.exists()) {
            setOpenAlertPopup(true);
            setPopupTitle(`포지션 데이터를 찾을 수 없습니다: ${itemId}`);
            setPopupButtonText('');
            setPopupFunction(() => {});
            continue;
          }
          const sourceData = sourceSnap.data();
          const targetRef = doc(db, `musicians/${selectedMusicard}/positions`, itemId);
          await setDoc(targetRef, {
            ...sourceData,
            positionId: itemId,
            ownerId: userID,
          }, { merge: true });
          // 2. 하위 "skills" 컬렉션 복사
          const sourceSkillsRef = collection(db, `users_data/${ userID }/positions/${itemId}/skills`);
          const skillsSnapshot = await getDocs(sourceSkillsRef);
          const batch = writeBatch(db);
          skillsSnapshot.forEach((skillDoc) => {
            const destSkillRef = doc(db, `musicians/${selectedMusicard}/positions/${itemId}/skills`, skillDoc.id);
            batch.set(destSkillRef, skillDoc.data());
          });
          await batch.commit();
          const changedPaths = [targetRef.path, sourceSkillsRef.path];
          await saveUserLog('musicard_logs', selectedMusicard, 'create', changedPaths, 'PushPosition');
          await setDoc(sourceRef, {
            act: { [selectedMusicard]: itemId }
          }, { merge: true });
          break;
        } else {
          console.error(`Unhandled attribute: ${attribute}`);
        }

        if (allItemsLength > 2) {
          currentProgress++;
          const percent = currentProgress / allItemsLength * 100;
          if (percent === 100) {
            setProgress(99);  
          } else {
            setProgress(((currentProgress / allItemsLength) * 100).toFixed(2));
          }
        }
      }

      isSubmitting.current = false;
      setShowProgress(false);
      setButtonLoading(false);
      setOpenPopup(false);
      setOpenAlertPopup(true);
      setPopupTitle(`선택한 ${attribute}들이 성공적으로 내보내졌습니다!`);
      setPopupButtonText('');
      setPopupFunction(() => {});
      fetchData();

      // 단일 선택 시 체크박스 초기화하지 않음
      if (selectedItem.length === 0) {
        clearSelectedItems([]); // 부모에서 전달한 선택 상태 초기화 함수 호출
      }
    } catch (error) {
      isSubmitting.current = false;
      setButtonLoading(false);
      setShowProgress(false);
      console.error(`${attribute} push error:`, error);
      setOpenAlertPopup(true);
      setPopupTitle(`Error pushing ${attribute}.`);
      setPopupButtonText('');
      setPopupFunction(() => {});
    }
  };

  // 🔹 로그인 확인  
  useEffect(() => {
    if (userLoading) return;
  
    if (user?.userID) {
      setUserID(user.userID);
    } else {
      navigate('/login');
    }
  }, [navigate, user?.userID, userLoading]);
  
  // 🔹 로그인 확인 후 뮤지카드 정보 가져오기
  useEffect(() => {
    if (!userID) return;
    fetchMusicards();
  }, [userID, fetchMusicards]);

  return (
    <>
    {
      missingProps
      ?
      (
      <div className={styleClasses.error}>
        <p>Error: 필요한 데이터(attribute 및 selectedItems)가 전달되지 않았습니다.</p>
      </div>
      )
      :
      (
      <>
        <div className={`${styleClasses.backDrop} ${active}`} onClick={() => { setActive(''); setOpenPopup(false); }} />
        <div className={`${styleClasses.pushAlbumPopup} ${active}`}>
          <div className={styleClasses.inner}>
            <div className={styleClasses.header}>
              <div className={styleClasses.title}>
                <h1>Push {attribute.charAt(0).toUpperCase() + attribute.slice(1)}</h1>
              </div>
              <div className={styleClasses.closeButton}>
                <button onClick={() => { setActive(''); setOpenPopup(false); }}>
                  <div className="commonIcon">
                    <i className="closeWhite w32"></i>
                  </div>
                </button>
              </div>
            </div>
            <div className={styleClasses.content}>
              <div className="commonForm">
                <div className="formRow fullWidth">
                  <div className="labelBox">
                    <label>내 뮤지카드 선택</label>
                  </div>
                  <div className="fieldBox">
                    <select value={selectedMusicard} onChange={(e) => setSelectedMusicard(e.target.value)}>
                      <option value="">뮤지카드를 선택하세요</option>
                      {musicards.map((card) => (
                        <option key={card.id} value={card.name}>
                          {card.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {categoryOptions.length > 0 && (
                  <div className="formRow fullWidth">
                    <div className="labelBox">
                      <label>어느 카테고리로 내보낼까요?</label>
                    </div>
                    <div className="fieldBox">
                      <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                        <option value="">세부 카테고리 선택</option>
                        {categoryOptions.map((option, idx) => (
                          <option key={idx} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {
                  showProgress
                  ?
                  (
                    <div className={ styleClasses.progressBarDiv }>
                      <div className={ styleClasses.progressBar }>
                        <div className={ styleClasses.progress }>
                          <div className={ styleClasses.inProgress } style={{ width: `${ progress }%` }}></div>
                          <div className={ styleClasses.percent}>
                            <span>{`${ progress }%`}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                  :
                  null
                }
                {selectedMusicard && (!(categoryOptions.length > 0) || selectedCategory) && (
                <div className="commonButton fullWidth">
                  <button onClick={handleSubmit} className="black" >
                        내보내기
                      <div className={`buttonLoading ${buttonLoading && 'active'}`}>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                  </button>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
      )
    }
    </>
  );
}

export default PushPopup;